import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch {
      setError('Credenciales incorrectas. Verifica tu email y contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-12 text-white"
        style={{ background: 'linear-gradient(145deg, #0094D9 0%, #006bb3 55%, #003f6e 100%)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg flex items-center justify-center">
            <img src="/logo-sidebar.png" alt="Life & Legacy" className="w-full h-full object-contain p-1" />
          </div>
          <div>
            <p className="font-bold text-xl">Life & Legacy</p>
            <p className="text-white/60 text-sm">CRM</p>
          </div>
        </div>

        <div>
          <h2 className="text-4xl font-bold leading-tight mb-4">
            Gestiona tu agencia<br />
            desde un solo lugar
          </h2>
          <p className="text-white/60 text-lg leading-relaxed">
            Prospectos, clientes, pólizas, citas y reportes — todo en tiempo real.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { icon: '📋', label: 'Pólizas', desc: 'Control total de vigencias' },
              { icon: '👥', label: 'Prospectos', desc: 'Pipeline de ventas' },
              { icon: '📅', label: 'Agenda', desc: 'Citas y seguimientos' },
              { icon: '📈', label: 'Reportes', desc: 'Métricas en tiempo real' },
            ].map(f => (
              <div key={f.label} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <p className="text-2xl mb-1">{f.icon}</p>
                <p className="font-semibold text-sm">{f.label}</p>
                <p className="text-white/50 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/30 text-xs">© {new Date().getFullYear()} Life & Legacy · Oaxaca, México</p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow flex items-center justify-center">
              <img src="/logo192.png" alt="Life & Legacy" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="font-bold text-primary-700">Life & Legacy</p>
              <p className="text-gray-400 text-xs">CRM</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="mb-7">
              <h3 className="text-2xl font-bold text-gray-900">Bienvenido</h3>
              <p className="text-gray-500 text-sm mt-1">Inicia sesión para continuar</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 transition"
                  placeholder="correo@ejemplo.com"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 transition pr-12"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(s => !s)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                  >
                    {showPass ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-60 shadow-md hover:shadow-lg active:scale-[0.99]"
                style={{ background: loading ? '#8fb93a' : 'linear-gradient(135deg, #0094D9, #A5CD39)' }}
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
